import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const SCROLL_EDGE_PX = 2;
/** 不足此溢出量时不提示，避免按钮几乎排下时被淡出盖住 */
const SCROLL_HINT_MIN_PX = 8;
/** 溢出达到约半个按钮时再出箭头 */
const SCROLL_CHEVRON_MIN_PX = 18;
const SCROLL_FADE_MAX_PX = 26;

const fadePxForOverflow = (overflow) => {
	if (overflow < SCROLL_HINT_MIN_PX) {
		return 0;
	}
	return Math.min(SCROLL_FADE_MAX_PX, Math.round(overflow + 8));
};

const readScrollEdges = (el) => {
	if (!el) {
		return { startPx: 0, endPx: 0 };
	}

	const { scrollLeft, scrollWidth, clientWidth } = el;
	const hiddenEnd = Math.max(0, scrollWidth - clientWidth - scrollLeft);
	const hiddenStart = Math.max(0, scrollLeft);
	const canScroll = scrollWidth > clientWidth + SCROLL_EDGE_PX;

	return {
		startPx: canScroll && hiddenStart > SCROLL_EDGE_PX ? fadePxForOverflow(hiddenStart) : 0,
		endPx: canScroll && hiddenEnd > SCROLL_EDGE_PX ? fadePxForOverflow(hiddenEnd) : 0,
		startChevron: canScroll && hiddenStart >= SCROLL_CHEVRON_MIN_PX,
		endChevron: canScroll && hiddenEnd >= SCROLL_CHEVRON_MIN_PX,
	};
};

/** 单个底部岛玻璃外壳。scrollHint 时，按钮超出可视宽度会在溢出侧给出可滑动暗示。 */
const MobileIslandActionIsland = ({ visible = true, children, className, ariaLabel = '快捷操作', single = false, scrollHint = false }) => {
	const innerRef = useRef(null);
	const [edges, setEdges] = useState({ startPx: 0, endPx: 0, startChevron: false, endChevron: false });

	useEffect(() => {
		if (!scrollHint) {
			setEdges({ startPx: 0, endPx: 0, startChevron: false, endChevron: false });
			return undefined;
		}

		const el = innerRef.current;
		if (!el) {
			return undefined;
		}

		let rafId = 0;
		const update = () => {
			setEdges((prev) => {
				const next = readScrollEdges(el);
				if (prev.startPx === next.startPx && prev.endPx === next.endPx && prev.startChevron === next.startChevron && prev.endChevron === next.endChevron) {
					return prev;
				}
				return next;
			});
		};
		const schedule = () => {
			if (rafId) {
				return;
			}
			rafId = window.requestAnimationFrame(() => {
				rafId = 0;
				update();
			});
		};

		const resizeObserver = new ResizeObserver(schedule);
		const observeBox = () => {
			resizeObserver.disconnect();
			resizeObserver.observe(el);
			Array.from(el.children).forEach((child) => resizeObserver.observe(child));
		};

		observeBox();
		update();
		el.addEventListener('scroll', schedule, { passive: true });
		const mutationObserver = new MutationObserver(() => {
			observeBox();
			schedule();
		});
		mutationObserver.observe(el, { childList: true, subtree: true });

		return () => {
			el.removeEventListener('scroll', schedule);
			if (rafId) {
				window.cancelAnimationFrame(rafId);
			}
			resizeObserver.disconnect();
			mutationObserver.disconnect();
		};
	}, [scrollHint]);

	if (!visible) {
		return null;
	}

	const canScroll = edges.startPx > 0 || edges.endPx > 0;

	return (
		<div
			className={classNames(
				'app-mobile-action-island',
				single && 'app-mobile-action-island--single',
				edges.endPx > 0 && 'app-mobile-action-island--scroll-end',
				edges.startPx > 0 && 'app-mobile-action-island--scroll-start',
				className,
			)}
			style={
				canScroll
					? {
							'--island-scroll-fade-end': `${edges.endPx}px`,
							'--island-scroll-fade-start': `${edges.startPx}px`,
						}
					: undefined
			}
			role='toolbar'
			aria-label={canScroll ? `${ariaLabel}，左右滑动查看更多` : ariaLabel}
		>
			<div ref={scrollHint ? innerRef : undefined} className='app-mobile-action-island__inner'>
				{children}
			</div>
			{edges.startChevron ? (
				<span className='app-mobile-action-island__scroll-hint app-mobile-action-island__scroll-hint--start' aria-hidden='true'>
					<FontAwesomeIcon icon='chevron-left' />
				</span>
			) : null}
			{edges.endChevron ? (
				<span className='app-mobile-action-island__scroll-hint app-mobile-action-island__scroll-hint--end' aria-hidden='true'>
					<FontAwesomeIcon icon='chevron-right' />
				</span>
			) : null}
		</div>
	);
};

MobileIslandActionIsland.propTypes = {
	visible: PropTypes.bool,
	children: PropTypes.node,
	className: PropTypes.string,
	ariaLabel: PropTypes.string,
	single: PropTypes.bool,
	scrollHint: PropTypes.bool,
};

export default MobileIslandActionIsland;
